import type { PairwiseDebt } from "@/types/Balance";
import type { GroupTransaction } from "@/types/GroupTransaction";

/**
 * Builds a group transaction that records a debt settlement:
 * debtor pays creditor the given amount in the given currency.
 * Payer = debtor. Split only to creditor.
 */
export function buildSettlementTransaction(debt: PairwiseDebt, name = "Debt payment"): GroupTransaction {
    const { debtor, creditor, currency, amount, sourceLiquidityPoolId, liquidityPoolId, settlementNote } = debt;
    const poolId = sourceLiquidityPoolId || liquidityPoolId;
    const timestamp = Date.now().toString();
    const splitDetails: Record<string, number> = { [creditor]: amount };
    const tx: GroupTransaction = {
        _id: timestamp,
        name,
        amount,
        payer: debtor,
        date: timestamp,
        currency,
        splitDetails,
        splitType: "custom",
        type: "settlement",
        settlementStatus: "pending_confirmation",
    };
    if (poolId) tx.sourceLiquidityPoolId = poolId;
    if (settlementNote) tx.settlementNote = settlementNote;
    return tx;
}

/** Effective settlement status (legacy settlements without status are confirmed). */
export function effectiveSettlementStatus(tx: GroupTransaction): import("@/types/Transaction").SettlementStatus | null {
    if (tx.type !== "settlement") return null;
    return tx.settlementStatus ?? "confirmed";
}

/** Creditor participant id from settlement split details. */
export function settlementCreditorId(tx: GroupTransaction): string | null {
    const details = tx.splitDetails ?? {};
    const entries = Object.entries(details).filter(([, v]) => Number(v) > 0);
    if (entries.length !== 1) return null;
    return entries[0][0];
}
